import { FormProvider, UseFormReturn, useWatch } from "react-hook-form";
import { WidgetDetailsFormValues } from "../WidgetDetails/useWidgetDetailsForm";
import { FormEvent } from "react";
import { Flex } from "styled-system/jsx";
import { ButtonCard, ComboboxLoader, PageHeader } from "shared/components";
import { AngleTool, ViewColumns3, ViewGrid } from "iconoir-react";
import { Eye } from "iconoir-react";
import { useTranslation } from "react-i18next";
import { EnumWidgetEventSettingsDisplayType } from "gql/generated";
import { Combobox, Select } from "@ttc3k/trekker";
import { SectionHeader, Text } from "@ttc3k/trekker";
import { useWidgetSetupEvents } from "./useWidgetSetupEvents";
import { AnimatePresence, motion } from "framer-motion";

type WidgetSetupEventsProps = {
  form: UseFormReturn<WidgetDetailsFormValues>;
  formId: string;
  onSubmit: (e?: FormEvent) => Promise<void>;
  onOpenPreviewModal: () => void;
};

export const WidgetSetupEvents = ({
  form,
  formId,
  onSubmit,
  onOpenPreviewModal,
}: WidgetSetupEventsProps) => {
  const { t } = useTranslation();

  const {
    lookAheadMonthsOptions,
    eventTagsLoaded,
    filteredTags,
    handleFilterTags,
    handleComboboxOpenChange,
  } = useWidgetSetupEvents();

  const displayType = useWatch({
    control: form.control,
    name: "eventSettings.displayType",
  });
  const eventTags = useWatch({
    control: form.control,
    name: "eventSettings.eventTags",
  });
  const lookAheadMonths = useWatch({
    control: form.control,
    name: "eventSettings.lookAheadMonths",
  });

  return (
    <Flex flexDir={"column"} gap={"500"}>
      <PageHeader
        title={`${t("widgets:PAGE.WIDGET")} ${t("widgets:DETAILS.TABS.SETUP")}`}
        description={t("widgets:DETAILS.TABS.DESCRIPTIONS.SETUP")}
        Icon={AngleTool}
        ctaButtonCopy={t("widgets:DETAILS.PAGE_ACTIONS.PREVIEW_WIDGET")}
        ctaButtonProps={{ Icon: Eye, onClick: onOpenPreviewModal }}
      />
      <FormProvider {...form}>
        <form id={formId} onSubmit={onSubmit}>
          <Flex
            flexDir="column"
            justifyContent="flex-start"
            alignItems="flex-start"
            gap="500"
          >
            {/* Widget display style */}
            <Flex flexDir="row" gap={"150"}>
              <ButtonCard
                title={t(
                  "widgets:CREATE_NEW.EVENT_WIDGET.WIDGET_STYLE.FULL_CALENDAR.TITLE",
                )}
                description={t(
                  "widgets:CREATE_NEW.EVENT_WIDGET.WIDGET_STYLE.FULL_CALENDAR.DESCRIPTION",
                )}
                Icon={ViewGrid}
                onClick={() => {
                  form.setValue(
                    "eventSettings.displayType",
                    EnumWidgetEventSettingsDisplayType.Grid,
                    { shouldDirty: true },
                  );
                  form.setValue("eventSettings.lookAheadMonths", 0, {
                    shouldDirty: true,
                  });
                }}
                isSelected={
                  displayType === EnumWidgetEventSettingsDisplayType.Grid
                }
              />
              <ButtonCard
                title={t(
                  "widgets:CREATE_NEW.EVENT_WIDGET.WIDGET_STYLE.CURATED_EVENTS.TITLE",
                )}
                description={t(
                  "widgets:CREATE_NEW.EVENT_WIDGET.WIDGET_STYLE.CURATED_EVENTS.DESCRIPTION",
                )}
                Icon={ViewColumns3}
                onClick={() => {
                  form.setValue(
                    "eventSettings.displayType",
                    EnumWidgetEventSettingsDisplayType.Carousel,
                    { shouldDirty: true },
                  );
                  form.setValue("eventSettings.lookAheadMonths", 12, {
                    shouldDirty: true,
                  });
                }}
                isSelected={
                  displayType === EnumWidgetEventSettingsDisplayType.Carousel
                }
              />
            </Flex>

            {/* Look ahead months (for Carousel only) */}
            <AnimatePresence mode="wait">
              {displayType === EnumWidgetEventSettingsDisplayType.Carousel && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.2 }}
                  style={{
                    transformOrigin: "top left",
                    width: "100%",
                    zIndex: 100,
                  }}
                >
                  <Flex flexDir={"column"} gap={"200"} w="full">
                    <SectionHeader
                      text={t(
                        "widgets:CREATE_NEW.EVENT_WIDGET.LOOK_AHEAD.TITLE",
                      )}
                      divider
                    />
                    <Text visual="bodyRegular" color="text.mid">
                      {t(
                        "widgets:CREATE_NEW.EVENT_WIDGET.LOOK_AHEAD.DESCRIPTION",
                      )}
                    </Text>
                    <Select.Root
                      items={lookAheadMonthsOptions}
                      value={[lookAheadMonths.toString()]}
                      onValueChange={({ value }) =>
                        form.setValue(
                          "eventSettings.lookAheadMonths",
                          parseInt(value[0]),
                          { shouldDirty: true },
                        )
                      }
                    >
                      {lookAheadMonthsOptions.map((option) => (
                        <Select.Item key={option.value} item={option} />
                      ))}
                    </Select.Root>
                  </Flex>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Selected filters */}

            <Flex flexDir={"column"} gap={"200"}>
              <SectionHeader
                text={t("widgets:CREATE_NEW.EVENT_WIDGET.FILTERS.TITLE")}
                divider
              />
              <Text visual="bodyRegular" color="text.mid">
                {t("widgets:CREATE_NEW.EVENT_WIDGET.FILTERS.DESCRIPTION")}
              </Text>
              {eventTagsLoaded && filteredTags.length > 0 ? (
                <Combobox.Root
                  items={filteredTags.flatMap((group) => group.tags)}
                  value={eventTags}
                  onValueChange={({ value }) =>
                    form.setValue("eventSettings.eventTags", value, {
                      shouldDirty: true,
                    })
                  }
                  onOpenChange={handleComboboxOpenChange}
                  onInputValueChange={(d) => handleFilterTags(d.inputValue)}
                  placeholder={t(
                    "widgets:CREATE_NEW.EVENT_WIDGET.FILTERS.PLACEHOLDER",
                  )}
                  multiple
                  positioning={{
                    placement: "top",
                  }}
                  tagVisualVariant={"accent.blue"}
                >
                  {filteredTags.length > 0 &&
                    filteredTags.map((tagGroup) => (
                      <Combobox.ItemGroup key={tagGroup.group}>
                        <Combobox.ItemGroupLabel>
                          {t(`events:APP.LOOKUPS.${tagGroup.group}`)}
                        </Combobox.ItemGroupLabel>
                        {tagGroup.tags.map((tag) => (
                          <Combobox.Item
                            paddingLeft={"300"}
                            key={tag.value}
                            item={tag}
                            label={tag.label}
                          />
                        ))}
                      </Combobox.ItemGroup>
                    ))}
                </Combobox.Root>
              ) : (
                <ComboboxLoader />
              )}
            </Flex>
          </Flex>
        </form>
      </FormProvider>
    </Flex>
  );
};
